import { TrendDownIcon, TrendUpIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

import {
  accentedWeights,
  componentKey,
  criteriaChips,
  emphasisDirection,
  emphasisKey,
  type CriteriaPayload,
  type WeightPreview,
} from '@entities/search-session';
import { KarlIntro } from '@shared/ui';

import styles from './ResultsSummary.module.css';

interface ResultsSummaryProps {
  /** `null` — критерии ещё не загружены: чипы просто не показываются. */
  criteria: CriteriaPayload | null;
  weights: readonly WeightPreview[];
  /** `undefined` — счёт ещё не известен. Ноль — это ноль, а не «загружается». */
  total: number | undefined;
  /**
   * Только заголовок экрана — витрина до оплаты (FE-586). Число, реплику Karl
   * и резюме запроса там несут реплика и плашка витрины по макету 03, и вторая
   * шапка с тем же числом была бы повтором.
   */
  titleOnly?: boolean | undefined;
}

/**
 * Шапка экрана результатов (FE-081, MVP_PRD §19).
 *
 * Содержит три обязательные вещи:
 *
 * * **счётчик и краткое резюме запроса** — чипами, где обязательные условия
 *   визуально отличаются от желательных;
 * * **итоговые веса при наличии акцентов** — «бюджет для вас критичен → его вес
 *   26 %». Числа приходят с backend посчитанными; снять акцент можно на экране
 *   критериев, а не здесь: правка критериев создаёт новую версию и новый прогон;
 * * **оговорку о границах системы** — результаты собраны из каталога CarViQ.DE,
 *   поиск в интернете не выполнялся. С редизайна 2026-10 её произносит Karl:
 *   вводный абзац экрана воронки — реплика `KarlIntro` (DESIGN_SYSTEM §7.2).
 */
export function ResultsSummary({
  criteria,
  weights,
  total,
  titleOnly = false,
}: ResultsSummaryProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'de';

  if (titleOnly) {
    return (
      <header className={styles.header}>
        <h1 className={styles.title}>{t('results.title')}</h1>
      </header>
    );
  }

  const chips =
    criteria === null ? [] : criteriaChips(criteria, (key, params) => t(key, params ?? {}), locale);
  const accents = accentedWeights(weights);

  return (
    <header className={styles.header}>
      <div className={styles.headline}>
        <h1 className={styles.title}>{t('results.title')}</h1>
        <p className={styles.count} aria-live="polite">
          {total === undefined
            ? t('results.countLoading')
            : t('results.countValue', { count: total })}
        </p>
      </div>

      {/* Оговорка о границах системы — вводная реплика Karl (DESIGN_SYSTEM
          §7.2): смысл прежний, текст фиксированный, из локалей. */}
      <KarlIntro>{t('results.intro')}</KarlIntro>

      {chips.length > 0 ? (
        <ul className={styles.chips} aria-label={t('results.criteriaLabel')}>
          {chips.map((chip) => (
            <li
              key={chip.key}
              className={[styles.chip, chip.strength === 'required' && styles.chipRequired]
                .filter(Boolean)
                .join(' ')}
            >
              {chip.label}
              {/* Жёсткость названа словом: рамка и цвет её не сообщают. */}
              <span className="visually-hidden"> — {t(`criteria.strength.${chip.strength}`)}</span>
            </li>
          ))}
        </ul>
      ) : null}

      {accents.length > 0 ? (
        <section className={styles.weights} aria-label={t('criteria.weights.title')}>
          <p className={styles.weightsTitle}>{t('criteria.weights.title')}</p>
          <ul className={styles.weightsList}>
            {accents.map((item) => {
              const direction = emphasisDirection(item.level);
              const DirectionIcon = direction === 'down' ? TrendDownIcon : TrendUpIcon;
              return (
                <li key={item.component} className={styles.weight}>
                  <DirectionIcon size={13} weight="bold" aria-hidden="true" />
                  <span>{t(componentKey(item.component))}</span>
                  <span className={styles.weightLevel}>{t(emphasisKey(item.level))}</span>
                  <span className={`${styles.weightShare} tabular`}>
                    {t('criteria.weights.share', { percent: item.share_percent })}
                  </span>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}
    </header>
  );
}
